import { getIOSVersion, isIOS } from '@core/platform'
import { Card, Screen } from '@design/components'

/**
 * Shown whenever the app is opened in a browser tab rather than from the
 * home screen.
 *
 * This is not a nag banner. Until the app is installed there is no push, and
 * without push the parent never learns that their invoice was handled — which
 * is the entire reason they would keep using this instead of WhatsApp. So the
 * app treats "not installed" as a blocking setup step, not a suggestion.
 */
export function InstallGuide() {
  const version = getIOSVersion()
  const tooOld = isIOS() && version !== null && (version[0] < 16 || (version[0] === 16 && version[1] < 4))

  return (
    <Screen className="flex flex-col justify-center max-w-md mx-auto w-full pad-safe-top">
      <h1 className="text-2xl font-semibold mb-2">Nog even installeren</h1>
      <p className="text-muted mb-6 leading-relaxed">
        Zet de app op je beginscherm. Anders kan je geen berichten ontvangen als
        er iets afgehandeld is.
      </p>

      {tooOld ? (
        <Card className="border-warn/40 bg-warn/5">
          <p className="font-medium mb-1">Deze iPhone is te oud</p>
          <p className="text-sm text-muted">
            Meldingen werken vanaf iOS 16.4. Deze telefoon heeft iOS{' '}
            {version?.join('.')}. Werk iOS bij via Instellingen → Algemeen →
            Software-update.
          </p>
        </Card>
      ) : (
        <Card>
          <ol className="space-y-4">
            <Step n={1}>
              Tik op <Share /> onderaan in Safari
            </Step>
            <Step n={2}>
              Kies <strong>Zet op beginscherm</strong>
            </Step>
            <Step n={3}>
              Tik op <strong>Voeg toe</strong>
            </Step>
            <Step n={4}>Open de app voortaan via het icoon op je beginscherm</Step>
          </ol>
        </Card>
      )}

      {!isIOS() && (
        <p className="text-sm text-muted mt-6">
          Op Android: menu (⋮) → <strong>App installeren</strong>.
        </p>
      )}
    </Screen>
  )
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex gap-3 items-start">
      <span className="shrink-0 w-7 h-7 rounded-full bg-ink text-canvas grid place-items-center text-sm font-medium">
        {n}
      </span>
      <span className="pt-0.5 leading-relaxed">{children}</span>
    </li>
  )
}

function Share() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-label="deel-knop"
      className="inline-block w-5 h-5 -mt-1 align-middle"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 3v12" />
      <path d="M8 7l4-4 4 4" />
      <path d="M5 13v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6" />
    </svg>
  )
}
